import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Form from 'react-bootstrap/Form';
import { useNavigate, Link } from 'react-router-dom'
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import toast from 'react-hot-toast';
import DatePicker from "react-datepicker";
import InputGroup from 'react-bootstrap/InputGroup';
import { calculateMaxHeightContent, formatNumber, unformatNumber } from '../../resources/functions';
import { OpennigBalanceFormI } from '../../resources/form-props';
import { OpenningBalanceValidate } from '../../resources/form-validator';
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook';
import { getBranch } from '../../store/user/selectors'
import { URLS } from '../../resources/constants';
import '../../assets/css/datepicker.css'
import './openning-balance.css'
import { glClassificationApi } from '../../resources/hooks/api/glClassificationApi';
import { getAccountSettings } from '../../store/settings/selectors'
import SAlert from '../../components/SAlert';
import CreateTransactionAdjustmentModal from '../../components/Modal/ViewRegister/CreateTransactionAdjustmentModal';
import { transTableDataApi } from '../../resources/hooks/api/transApiHook';
import { useTranslation } from 'react-i18next';

interface Props {

}

const OpenningBalance: React.FC<Props> = () => {
    const { t } = useTranslation('scenes.OpenningBalance.index');
    const branchData = useSelector(getBranch)
    const { fetchOpenningAcc, postOpenningAcc, glAccountsList } = useChartAccountApi();
    const { fetchAccountSettingsData } = useSettingsApi();
    const { skipBeginningBalance } = glClassificationApi();
    const { createTransactionAdjustment } = transTableDataApi()
    const navigate = useNavigate();

    const _getAccountSettings = useSelector(getAccountSettings);

    const calendarRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalAssets, setTotalAssets] = useState<number>(0);
    const [totalLiabilities, setTotalLiabilities] = useState<number>(0);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [showCreateAdjustmentModal, setShowCreateAdjustmentModal] = useState(false);
    const [glAllAccount, setGlAllAccount] = useState<any[]>([]);
    const [showConfirm, setShowConfirm] = useState<{
        submitForm: boolean,
        skipForm: boolean
    }>({
        submitForm: false,
        skipForm: false
    })
    const [selectedFilter, setSelectedFilter] = useState<any>({
        endDate: moment().endOf('month').format('YYYY-MM-DD')
    });
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);

    const { register, control, setValue, getValues, reset, handleSubmit, formState: { errors } } = useForm<OpennigBalanceFormI>({
        defaultValues: {
            assets: [],
            liabilities: []
        },
        resolver: yupResolver(OpenningBalanceValidate),
    });
    const { fields: assetFields, append: addMoreAsset, remove: deleteAsset } = useFieldArray({
        control,
        name: 'assets',
    });
    const { fields: liabilitiesFields, append: addMoreLiabilities, remove: deleteLiabilities } = useFieldArray({
        control,
        name: 'liabilities',
    });

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(78));
    })

    useEffect(() => {
        fetchList();
    }, [selectedFilter['endDate']])

    const resetForm = () => {
        setTotalAssets(0);
        setTotalLiabilities(0);

        reset({
            assets: [],
            liabilities: []
        });
    }

    const _handleChangeSearchParam = (e: any, type: string) => {
        if (moment(e).isValid()) {
            setSelectedFilter({
                'endDate': moment(e).endOf('month').format('YYYY-MM-DD')
            })
        }
    }

    const handleAssetsInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        let inputValue = e.target.value.replace(/[^0-9.-]/g, '');
        const isNegative = inputValue.startsWith('-');

        // Allow input of negative numbers
        if (isNegative) {
            inputValue = inputValue.substring(1);
        }

        const formattedValue = formatNumber(inputValue);

        const result = isNegative ? `-${formattedValue}` : formattedValue;

        setValue(`assets.${index}.opening_balance`, result);
        calculateTotalAsset();
    };

    const handleLiabilitiesInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        let inputValue = e.target.value.replace(/[^0-9.-]/g, '');
        const isNegative = inputValue.startsWith('-');

        // Allow input of negative numbers
        if (isNegative) {
            inputValue = inputValue.substring(1);
        }

        const formattedValue = formatNumber(inputValue);

        const result = isNegative ? `-${formattedValue}` : formattedValue;

        setValue(`liabilities.${index}.opening_balance`, result);
        calculateTotalBSLiabilities();
    };

    const openCalendar = () => {
        if (calendarRef.current) {
            calendarRef.current?.setOpen(true)
        }
    }

    const fetchList = () => {
        resetForm();

        const nextMonthDate = moment(selectedFilter['endDate']).format('YYYY-MM-DD');
        const params = {
            month: +moment(nextMonthDate).format('M'),
            year: +moment(nextMonthDate).format('Y'),
            branch_id: +branchData['id']
        }
        setIsLoading(true);
        fetchOpenningAcc(params, (message: string, resp: any) => {
            setIsLoading(false);

            let totalAsset = 0, totalLiabilities = 0;
            resp.data.assets.map((data: any) => {
                totalAsset += data['opening_balance'] || 0;
                addMoreAsset({
                    opening_balance: data['opening_balance'] ? formatNumber('' + data['opening_balance']) : '',
                    account: data['account'],
                    name: data['name'],
                    description: data['description'],
                    report_code: data['report_code'],
                    financial_statement: data['financial_statement'],
                    master_chart_of_account_id: data['master_chart_of_account_id'],
                    credit: data['credit'] || '',
                    debit: data['debit'] || '',
                })
            })
            resp.data.liabilities.map((data: any) => {
                totalLiabilities += data['opening_balance'] || 0;
                addMoreLiabilities({
                    opening_balance: data['opening_balance'] ? formatNumber('' + data['opening_balance']) : '',
                    account: data['account'],
                    name: data['name'],
                    description: data['description'],
                    report_code: data['report_code'],
                    financial_statement: data['financial_statement'],
                    master_chart_of_account_id: data['master_chart_of_account_id'],
                    credit: data['credit'] || '',
                    debit: data['debit'] || '',
                })
            })
            console.log('totalLiabilities: ', totalLiabilities)
            setTotalAssets(parseFloat(totalAsset.toFixed(2)));
            setTotalLiabilities(parseFloat(totalLiabilities.toFixed(2)));
            // setIsEdit(resp.data.is_exists || false);
            // setIsEdit(false);

            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100)
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const _handleSubmit = (data: any) => {
        const queryString = {
            branch_id: +branchData['id'],
            month: +moment(selectedFilter['endDate']).format('M'),
            year: +moment(selectedFilter['endDate']).format('Y')
        }
        const formData = {
            "opening_balance": [
                ...data.assets.filter((e: any) => e['opening_balance'] != 0).map((e: any) => ({
                    ...e,
                    opening_balance: unformatNumber(e['opening_balance'])
                })),
                ...data.liabilities.filter((e: any) => e['opening_balance'] != 0).map((e: any) => ({
                    ...e,
                    opening_balance: unformatNumber(e['opening_balance'])
                })),
            ],
            "total_assets": totalAssets,
            "total_liabilities": totalLiabilities
        }

        if (totalAssets !== totalLiabilities) {
            toast.error('Total Assets and Total Liabilities must be equal');
            return;
        }

        setIsLoading(true)
        postOpenningAcc({ queryString, formData }, (message: string, resp: any) => {
            setIsLoading(false)
            toast.success(message);
            fetchList();
            getSettingsData();
        }, (message: string, resp: any) => {
            setIsLoading(false)
            toast.error(message);
        })
    }

    const submitForm = () => {
        setShowConfirm({ submitForm: true, skipForm: false });
        // handleSubmit(_handleSubmit)();
    };

    const calculateTotalAsset = () => {
        let total = 0;
        getValues('assets').map((item: any) => {
            total += item.account != 3999 ? (unformatNumber(item.opening_balance) || 0) : 0;
        })

        const gotIndex = getValues('assets').findIndex((item: any) => item.account == 3999);
        setTotalAssets(parseFloat(total.toFixed(2)))
        setValue(`assets.${gotIndex}.opening_balance`, '' + total);
    }
    const calculateTotalBSLiabilities = () => {
        let total = 0;
        getValues('liabilities').map((item: any) => {
            total += item.account != 5999 ? (unformatNumber(item.opening_balance) || 0) : 0;
        })
        const gotIndex = getValues('liabilities').findIndex((item: any) => item.account == 5999);
        setTotalLiabilities(parseFloat(total.toFixed(2)));
        setValue(`liabilities.${gotIndex}.opening_balance`, '' + total);
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            event.preventDefault();
        }
    };

    const getSettingsData = () => {
        fetchAccountSettingsData({ branch_id: +branchData['id'] }, (msg: string, resp: any) => {

        }, (msg: string, resp: any) => {

        })
    }

    const completeCurrentStep = (message: string) => {
        setIsLoading(true);
        skipBeginningBalance({ 
            branch_id: +branchData['id'],
            month: +moment(selectedFilter['endDate']).format('M'),
            year: +moment(selectedFilter['endDate']).format('Y')
        }, (message: string, resp: any) => {
          setIsLoading(false);
          toast.success(message)
          navigate(URLS.DASHBOARD)
        }, (message: string, resp: any) => {
          setIsLoading(false);
          toast.error(message);
        })
    }

    const submitAdjustment = (data: any) => {
        setIsLoading(true);
        const params: any = data.map((e: any) => ({
            "description": e.description,
            "transaction_at": moment(e.transaction_at).format('YYYY-MM-DDTHH:mm:ss'),
            "adjustment_debit": e.adjustment_debit,
            "adjustment_credit": e.adjustment_credit,
            "adjustment_master_debit_account_id": e.adjustment_master_debit_account_id.value,
            "adjustment_master_credit_account_id": e.adjustment_master_credit_account_id.value
        }))

        createTransactionAdjustment({ params, branch_id: +branchData['id'], is_beginning_cash: 1 }, (message: string, resp: any) => {
            setIsLoading(false);
            completeCurrentStep(message);
            //toast.success(message)
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    };

    const fechAccountList = (searchText: string = ''): Promise<any[]> => {
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }

            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id']
                }));
                return resolve(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    const filterAllAccountListExceptBankSales = async (inputValue: string) => {
        const data = await fechAccountList(inputValue)
        return data;
    };

    const handleAdjustmentClick = () => {
        setShowCreateAdjustmentModal(true);
    };

    useEffect(() => {
        if ( _getAccountSettings.opening_balance_created_at || _getAccountSettings.opening_balance_skipped_at ) {
            setSelectedFilter({ endDate: moment(_getAccountSettings.opening_balance_created_at || _getAccountSettings.opening_balance_skipped_at).endOf('month').format('YYYY-MM-DD') });
            setIsEdit(false);
        } else {
            setIsEdit(true);
        }
    }, [_getAccountSettings.opening_balance_created_at, _getAccountSettings.opening_balance_skipped_at])

    useEffect(() => {
        fechAccountList('').then(r => {
            setGlAllAccount(r);
        })
    }, [])

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-4 pt-3">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h3 font-weight-700 mb-0 d-inline-flex">{t('title')}</h1>
                        </div>
                    </div>
                </div>
                <div className="executive-dashboard">
                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                        <div className="loader"></div>
                    </div>
                    <div className="row">
                        <div className="align-items-end col-md-7 d-flex gap-2 justify-content-end">
                            {
                                !_getAccountSettings.is_beginning_balance_exists &&
                                (<Link to="javascript:void(0)" className="d-flex gap-2 align-items-center btn btn-cancel mb-3" onClick={handleAdjustmentClick}>
                                    <span>{t('button.beginningCashOnly')}</span>
                                </Link>) || <></>
                            }
                            <div className="filter-box form-group date-picker-container mb-2">
                                <label htmlFor="">{t('label.endingDate')}<strong className='text-danger'>*</strong></label>
                                <DatePicker
                                    ref={calendarRef}
                                    selected={moment(selectedFilter['endDate']).toDate()}
                                    onChange={(date: Date) => _handleChangeSearchParam(date, 'endDate')}
                                    className='form-control'
                                    dateFormat={'MM/dd/yyyy'}
                                    maxDate={new Date()}
                                    showMonthYearPicker
                                    calendarIconClassname='tex-end'
                                    disabled={!!_getAccountSettings.is_beginning_balance_exists}
                                />

                                <i className="fa fa-calendar fa-2xl cursor-pointer calendar-icon" aria-hidden="true" onClick={openCalendar}></i>
                            </div>
                        </div>
                    </div>


                    <div style={{ padding: 0, margin: 0, textAlign: 'center' }}>
                        <div className='col-md-7 d-flex justify-content-center'>

                        <Form className='w-100' onKeyDown={handleKeyDown} /*onSubmit={handleSubmit(saveMonthlyIncome)}*/>
                            <div>
                                <table className="openning-balance table boder-padding-none">
                                    <tr>
                                        <td colSpan={12}>
                                            <table className="section-table">
                                                <tr className="header">
                                                    <td width={'10%'}>{t('label.account')}</td>
                                                    <td width={'60%'}>{t('label.name')}</td>
                                                    <td width={'40%'}>{t('label.amount')} (in $)</td>
                                                </tr>
                                                {assetFields.map((item: any, index: number) => (
                                                    <tr key={item.id} className={`${['GH', 'SH', "SSH"].includes(item.report_code) ? 'grey-background' : ''} ${['GT', 'ST', 'SST'].includes(item.report_code) ? 'total-row' : ''}`}>
                                                        <td className='text-start'>
                                                            <label htmlFor="">
                                                                {
                                                                    ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                        getValues(`assets.${index}.account`) : getValues(`assets.${index}.account`) == 3999 ? (<span style={{ fontSize: 'larger' }}>{getValues(`assets.${index}.account`)}</span>) : ['GT', 'ST', 'SST'].includes(item.report_code) ? (
                                                                            <span></span>
                                                                        ) : <span>{getValues(`assets.${index}.account`)}</span>
                                                                }
                                                            </label>
                                                        </td>
                                                        <td className='text-start'>
                                                            {
                                                                ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                    <input
                                                                        type='text'
                                                                        {...register(`assets.${index}.name`)}
                                                                        placeholder={t('placeholder.name')}
                                                                        readOnly={!isEdit}
                                                                        className={`form-control ${errors && errors.assets && errors.assets[index]?.name ? 'is-invalid' : ''} ${isEdit ? '' : 'input-disabled-look'}`}
                                                                    /> : <label htmlFor="">
                                                                        {
                                                                            getValues(`assets.${index}.account`) == 3999 ?
                                                                                <span style={{ fontSize: 'larger' }}>
                                                                                    {getValues(`assets.${index}.name`)}
                                                                                </span> :
                                                                                <span>
                                                                                    {getValues(`assets.${index}.name`)}
                                                                                </span>
                                                                        }
                                                                    </label>
                                                            }
                                                        </td>
                                                        {
                                                            ['VP', 'IP', "VP/IP"].includes(item.report_code) ? (
                                                                <td>
                                                                    <InputGroup hasValidation className="input-group-custom">
                                                                        <InputGroup.Text>$</InputGroup.Text>
                                                                        <input
                                                                            type='text'
                                                                            {...register(`assets.${index}.opening_balance`)}
                                                                            placeholder={t('placeholder.amount')}
                                                                            onBlur={(e) => {
                                                                                handleAssetsInputChange(e, index); 
                                                                                // Call the function on blur
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault();
                                                                                    let nextIndex = index + 1;
                                                                                    while (nextIndex < assetFields.length) {
                                                                                        const nextField = getValues(`assets.${nextIndex}`);
                                                                                        if (['VP', 'IP', 'VP/IP'].includes(nextField.report_code)) {
                                                                                            const nextInput = document.querySelector(`input[name="assets.${nextIndex}.opening_balance"]`);
                                                                                            if (nextInput) {
                                                                                                (nextInput as HTMLInputElement).focus();
                                                                                                break;
                                                                                            }
                                                                                        }
                                                                                        nextIndex++;
                                                                                    }

                                                                                    if ( nextIndex >= assetFields.length ) {
                                                                                        let liabilityIndex = 0
                                                                                        while (liabilityIndex < liabilitiesFields.length) {
                                                                                            const nextField = getValues(`liabilities.${liabilityIndex}`);
                                                                                            if (['VP', 'IP', 'VP/IP'].includes(nextField.report_code)) {
                                                                                                const nextInput = document.querySelector(`input[name="liabilities.${liabilityIndex}.opening_balance"]`);
                                                                                                if (nextInput) {
                                                                                                    (nextInput as HTMLInputElement).focus();
                                                                                                    break;
                                                                                                }
                                                                                            }
                                                                                            liabilityIndex++;
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }}
                                                                            readOnly={!isEdit}
                                                                            className={`form-control ${isEdit ? '' : 'input-disabled-look'}`}
                                                                        />
                                                                    </InputGroup>
                                                                </td>
                                                            ) : <td className='text-start'>
                                                                <label htmlFor="">
                                                                    {
                                                                        getValues(`assets.${index}.account`) == 3999 ? <strong style={{ fontSize: 'larger' }}>{'$' + formatNumber(parseFloat('' + totalAssets).toFixed(2))}</strong> : ''
                                                                    }
                                                                </label>
                                                            </td>
                                                        }
                                                    </tr>
                                                ))}
                                                {liabilitiesFields.map((item: any, index: number) => (
                                                    <tr key={item.id} className={`${['GH', 'SH', "SSH"].includes(item.report_code) ? 'grey-background' : ''} ${['GT', 'ST', 'SST'].includes(item.report_code) ? 'total-row' : ''}`}>
                                                        <td className='text-start'>
                                                            <label htmlFor="">
                                                                {
                                                                    ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                        getValues(`liabilities.${index}.account`) : getValues(`liabilities.${index}.account`) == 5999 ? (<span style={{ fontSize: 'larger' }}>{getValues(`liabilities.${index}.account`)}</span>) : ['GT', 'ST', 'SST'].includes(item.report_code) ? (
                                                                            <span></span>
                                                                        ) : <span>{getValues(`liabilities.${index}.account`)}</span>
                                                                }
                                                            </label>
                                                        </td>
                                                        <td className='text-start'>
                                                            {
                                                                ['VP', 'IP', "VP/IP"].includes(item.report_code) ?
                                                                    <input
                                                                        type='text'
                                                                        {...register(`liabilities.${index}.name`)}
                                                                        placeholder={t('placeholder.name')}
                                                                        readOnly={!isEdit}
                                                                        className={`form-control ${errors && errors.liabilities && errors.liabilities[index]?.name ? 'is-invalid' : ''} ${isEdit ? '' : 'input-disabled-look'}`}
                                                                    /> : <label htmlFor="">
                                                                        {
                                                                            getValues(`liabilities.${index}.account`) == 5999 ?
                                                                                <span style={{ fontSize: 'larger' }}>
                                                                                    {getValues(`liabilities.${index}.name`)}
                                                                                </span> :
                                                                                <span>
                                                                                    {getValues(`liabilities.${index}.name`)}
                                                                                </span>
                                                                        }
                                                                    </label>
                                                            }
                                                        </td>
                                                        {
                                                            ['VP', 'IP', "VP/IP"].includes(item.report_code) ? (
                                                                <td>
                                                                    <InputGroup hasValidation className="input-group-custom">
                                                                        <InputGroup.Text>$</InputGroup.Text>
                                                                        <input
                                                                            type='text'
                                                                            {...register(`liabilities.${index}.opening_balance`)}
                                                                            placeholder={t('placeholder.amount')}
                                                                            onBlur={(e) => {
                                                                                handleLiabilitiesInputChange(e, index); 
                                                                                // Call the function on blur
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault();
                                                                                    let nextIndex = index + 1;
                                                                                    while (nextIndex < liabilitiesFields.length) {
                                                                                        const nextField = getValues(`liabilities.${nextIndex}`);
                                                                                        if (['VP', 'IP', 'VP/IP'].includes(nextField.report_code)) {
                                                                                            const nextInput = document.querySelector(`input[name="liabilities.${nextIndex}.opening_balance"]`);
                                                                                            if (nextInput) {
                                                                                                (nextInput as HTMLInputElement).focus();
                                                                                                break;
                                                                                            }
                                                                                        }
                                                                                        nextIndex++;
                                                                                    }
                                                                                }
                                                                            }}
                                                                            readOnly={!isEdit}
                                                                            className={`form-control ${isEdit ? '' : 'input-disabled-look'}`}
                                                                        />
                                                                    </InputGroup>
                                                                </td>
                                                            ) : <td className='text-start'>
                                                                <label htmlFor="">
                                                                    {
                                                                        getValues(`liabilities.${index}.account`) == 5999 ? <strong style={{ fontSize: 'larger' }}>{'$' + formatNumber(parseFloat('' + totalLiabilities).toFixed(2))}</strong> : ''
                                                                    }
                                                                </label>
                                                            </td>
                                                        }
                                                    </tr>
                                                ))}
                                            </table>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            {
                                !_getAccountSettings.is_beginning_balance_exists ? (
                                    <button type="button" onClick={submitForm} className="btn btn-primary mt-4">{t('button.saveBeginningBalance')}</button>
                                ) : <></>
                            }
                        </Form>
                        </div>
                    </div>

                </div>
            </div>
            <SAlert
                show={showConfirm.skipForm || showConfirm.submitForm}
                icon={'warning'}
                showCancel={true}
                confirmBtnText={t("msg.confirm.btn")}
                cancelBtnText={t("msg.cancel.btn")}
                confirmBtnBsStyle={'danger'}
                cancelBtnBsStyle={"secondary"}
                title={`${t('msg.confirmTitle')} ${ showConfirm.skipForm ? t('msg.skip') : t('msg.submit') } ${t('msg.beginningBalances')} ${moment(selectedFilter['endDate']).format('MMMM, YYYY')}?`}
                msg={t('msg.noReenter')}
                onConfirm={() => {
                    if ( showConfirm.skipForm ) {
                        completeCurrentStep('');
                        setShowConfirm({ submitForm: false, skipForm: false });
                    } else {
                        handleSubmit(_handleSubmit)()
                        setShowConfirm({ submitForm: false, skipForm: false });
                    }
                }}
                onCancel={() => setShowConfirm({ submitForm: false, skipForm: false })}
            />

            {
                showCreateAdjustmentModal ?
                    <CreateTransactionAdjustmentModal
                        show={showCreateAdjustmentModal}
                        glAccounts={glAllAccount}
                        onHide={() => setShowCreateAdjustmentModal(false)}
                        onSave={submitAdjustment}
                        filterAllAccountList={filterAllAccountListExceptBankSales}
                        selectedCalendar={{
                            year: selectedFilter['endDate'] ? moment(selectedFilter['endDate']).year() : moment().year(),
                            month: selectedFilter['endDate'] ? moment(selectedFilter['endDate']).month() + 1 : moment().month() + 1,
                            day: selectedFilter['endDate'] ? moment(selectedFilter['endDate']).date() : moment().date()
                        }}
                        callingType='openning_cash'
                    /> : <></>
            }
        </div>

    )
}

export default OpenningBalance;